import type {
  ObservabilityCompareBucket,
  ObservabilityPercentiles,
  ObservabilityRunFact,
  ObservabilitySpanFact,
} from '@scatterin/api-types';
import { ArrowLeft, ArrowRight, Clock3, Gauge, ScrollText } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { JsonBlock } from '@/components/codeBlock';
import { DetailList, KeyValueGrid } from '@/components/keyValueGrid';
import { EmptyState, ErrorState, LoadingState, StateView } from '@/components/stateViews';
import { RuntimeBadge, StatusBadge } from '@/components/statusBadges';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Drawer } from '@/components/ui/drawer';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableShell } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePerformanceCompareQuery, useRunDetailQuery, useRunSearchQuery, useSummaryQuery } from '@/hooks/useObservabilityQueries';
import { useRouteContext } from '@/hooks/useRouteContext';
import { formatBytes, formatCount, formatDuration, formatInteger, formatRelativeTime, formatTime } from '@/lib/format';
import { drillThroughSearch } from '@/lib/navigation';
import type { AnalyticsUrlState } from '@/lib/urlState';

type PerformanceView = 'runs' | 'startup' | 'timing';

const performanceViews = ['runs', 'timing', 'startup'] as const;

const humanize = (value: string | null | undefined): string => {
  if (!value) return 'Not recorded';
  return value
    .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
    .replaceAll(/[-_]+/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim()
    .replace(/^./, (character) => character.toUpperCase());
};

const durationLabel = (value: number | null | undefined, unit = 'ms'): string =>
  typeof value === 'number' ? formatDuration(value, unit) : 'Not recorded';

const recordedInteger = (value: number | null | undefined): string => (typeof value === 'number' ? formatInteger(value) : 'Not recorded');

const recordedBytes = (value: number | null | undefined): string => (typeof value === 'number' ? formatBytes(value) : 'Not recorded');

const rateLabel = (value: number | null | undefined): string =>
  typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : 'Not measured';

const driftTone = (direction: string): 'green' | 'red' | 'slate' => {
  if (direction === 'slower') return 'red';
  if (direction === 'faster') return 'green';
  return 'slate';
};

const workerVersionLabel = (value: string | null | undefined): string => (value && value !== 'unknown' ? value : 'Not recorded');

const runTitle = (run: ObservabilityRunFact): string => humanize(run.analysisType ?? run.jobKind ?? run.serviceName ?? 'Run');

const runSubtitle = (run: ObservabilityRunFact): string =>
  [run.serviceName, run.jobKind ? humanize(run.jobKind) : null].filter(Boolean).join(' · ') || 'Service not recorded';

const currentPerformanceView = (search: string): PerformanceView => {
  const view = new URLSearchParams(search).get('view');
  return performanceViews.includes(view as PerformanceView) ? (view as PerformanceView) : 'runs';
};

const updateSearch = (current: string, updates: Record<string, string | null>): string => {
  const search = new URLSearchParams(current);
  for (const [key, value] of Object.entries(updates)) {
    if (value) search.set(key, value);
    else search.delete(key);
  }
  return search.toString();
};

const PercentileChart = ({ label, percentiles }: { label: string; percentiles: ObservabilityPercentiles | null | undefined }) => {
  const markerClasses = ['bg-chart-secondary', 'bg-accent', 'bg-deep'];
  const markers = [
    { key: 'p50', label: 'P50', value: percentiles?.p50 },
    { key: 'p95', label: 'P95', value: percentiles?.p95 },
    { key: 'p99', label: 'P99', value: percentiles?.p99 },
  ].filter((marker): marker is { key: string; label: string; value: number } => typeof marker.value === 'number');
  const maximum = Math.max(...markers.map((marker) => marker.value), 0);

  if ((percentiles?.count ?? 0) === 0 || maximum === 0) {
    return (
      <EmptyState title={`No ${label.toLowerCase()} samples`} tone="coverage">
        Percentiles appear only after this duration is recorded in the selected scope.
      </EmptyState>
    );
  }

  return (
    <div className="grid gap-4">
      <div
        aria-label={`${label} distribution annotations: ${markers
          .map((marker) => `${marker.label} ${durationLabel(marker.value, percentiles?.unit ?? 'ms')}`)
          .join(', ')}`}
        className="grid gap-3"
        role="img"
      >
        <div className="bg-line relative mt-5 h-1 rounded-full">
          {markers.map((marker, index) => (
            <span
              className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
              key={marker.key}
              style={{ left: `${Math.min((marker.value / maximum) * 100, 100)}%` }}
            >
              <span className={`border-raised block size-3 rounded-full border-2 ${markerClasses[index] ?? 'bg-deep'}`} />
            </span>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {markers.map((marker) => (
            <div className="border-line bg-raised/55 rounded-md border p-2 text-center" key={marker.key}>
              <p className="text-muted text-[0.6875rem] font-semibold">{marker.label}</p>
              <p className="text-ink numeric mt-1 text-sm font-semibold">{durationLabel(marker.value, percentiles?.unit ?? 'ms')}</p>
            </div>
          ))}
        </div>
      </div>
      <p className="text-muted text-xs">
        {formatInteger(percentiles?.count)} samples · markers share one duration scale; they are not separate scores.
      </p>
    </div>
  );
};

const StageComparison = ({
  percentile,
  summary,
}: {
  percentile: AnalyticsUrlState['percentile'];
  summary: ReturnType<typeof useSummaryQuery>['data'];
}) => {
  const stages = [
    {
      key: 'queue',
      label: 'Queue wait',
      value: summary?.runs.durations.queueWaitDuration[percentile],
      unit: summary?.runs.durations.queueWaitDuration.unit ?? 'ms',
    },
    {
      key: 'startup',
      label: 'Startup',
      value: summary?.runs.durations.startupDuration[percentile],
      unit: summary?.runs.durations.startupDuration.unit ?? 'ms',
    },
    {
      key: 'run',
      label: 'End to end',
      value: summary?.runs.durations.runDuration[percentile],
      unit: summary?.runs.durations.runDuration.unit ?? 'ms',
    },
  ];
  const maximum = Math.max(...stages.map((stage) => stage.value ?? 0), 0);

  return (
    <div className="grid gap-3">
      {stages.map((stage) => (
        <div className="grid gap-1.5" key={stage.key}>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-ink font-medium">{stage.label}</span>
            <span className="text-muted numeric">{durationLabel(stage.value, stage.unit)}</span>
          </div>
          <div className="bg-line h-2 overflow-hidden rounded-full">
            <div
              className="bg-chart-secondary h-full rounded-full"
              style={{ width: `${maximum && stage.value ? Math.max((stage.value / maximum) * 100, 2) : 0}%` }}
            />
          </div>
        </div>
      ))}
      <p className="text-muted text-xs">
        {percentile.toUpperCase()} values share one axis. Percentiles are calculated independently and must not be added or subtracted as if
        they describe the same run.
      </p>
    </div>
  );
};

const RankedDurationList = ({
  buckets,
  emptyTitle,
  label,
}: {
  buckets: ObservabilityCompareBucket[];
  emptyTitle: string;
  label: string;
}) => {
  const ranked = buckets
    .filter((bucket) => typeof bucket.values?.p95 === 'number')
    .toSorted((left, right) => (right.values?.p95 ?? 0) - (left.values?.p95 ?? 0));
  const maximum = Math.max(...ranked.map((bucket) => bucket.values?.p95 ?? 0), 0);

  if (ranked.length === 0) {
    return (
      <EmptyState title={emptyTitle} tone="coverage">
        This ranking requires recorded duration samples in the selected scope.
      </EmptyState>
    );
  }

  return (
    <ol className="grid gap-3">
      {ranked.map((bucket, index) => (
        <li className="grid gap-1.5" key={bucket.key}>
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-ink min-w-0 font-medium">
              <span className="text-muted numeric mr-2">{index + 1}</span>
              {humanize(bucket.key)}
            </span>
            <span className="text-muted numeric shrink-0">{durationLabel(bucket.values?.p95, bucket.values?.unit ?? 'ms')}</span>
          </div>
          <div className="bg-line h-1.5 overflow-hidden rounded-full">
            <div
              className="bg-accent h-full rounded-full"
              style={{ width: `${maximum ? Math.max(((bucket.values?.p95 ?? 0) / maximum) * 100, 2) : 0}%` }}
            />
          </div>
          <p className="text-muted text-xs">{formatCount(bucket.count, `${label} sample`)} · P95</p>
        </li>
      ))}
    </ol>
  );
};

const RunTimeline = ({ run }: { run: ObservabilityRunFact }) => {
  const events = [
    { key: 'queued', label: 'Queued', value: run.queuedAt },
    { key: 'started', label: 'Worker started', value: run.startedAt },
    { key: 'progress', label: 'First progress', value: run.firstProgressAt },
    { key: 'terminal', label: 'Terminal', value: run.terminalAt },
    { key: 'observed', label: 'Last observed', value: run.observedAt },
  ].filter((event): event is { key: string; label: string; value: string } => Boolean(event.value));
  const baseline = events[0] ? new Date(events[0].value).getTime() : 0;

  return (
    <ol className="border-line grid divide-y rounded-lg border">
      {events.map((event) => {
        const elapsed = Math.max(new Date(event.value).getTime() - baseline, 0);
        return (
          <li className="grid gap-1 px-3 py-2 text-sm sm:grid-cols-[9rem_1fr_auto] sm:items-center" key={event.key}>
            <span className="text-ink font-medium">{event.label}</span>
            <span className="text-muted">{formatTime(event.value)}</span>
            <span className="text-muted numeric">{event.key === events[0]?.key ? 'Start' : `+${durationLabel(elapsed)}`}</span>
          </li>
        );
      })}
    </ol>
  );
};

const SpanWaterfall = ({ spans }: { spans: ObservabilitySpanFact[] }) => {
  const timed = spans.filter((span) => typeof span.durationMs === 'number');
  const baseline = Math.min(...timed.map((span) => new Date(span.startedAt ?? span.observedAt).getTime()));
  const maximum = Math.max(
    ...timed.map((span) => new Date(span.startedAt ?? span.observedAt).getTime() - baseline + (span.durationMs ?? 0)),
    0,
  );

  if (timed.length === 0 || !Number.isFinite(baseline) || maximum === 0) {
    return (
      <EmptyState title="No span timing was recorded" tone="coverage">
        The selected run has no duration-bearing spans to place on a waterfall.
      </EmptyState>
    );
  }

  return (
    <ol className="grid gap-3">
      {timed.map((span) => {
        const offset = new Date(span.startedAt ?? span.observedAt).getTime() - baseline;
        const left = Math.max((offset / maximum) * 100, 0);
        const width = Math.max(((span.durationMs ?? 0) / maximum) * 100, 1.5);
        return (
          <li className="grid gap-1.5" key={span.id}>
            <div className="flex items-start justify-between gap-3 text-sm">
              <span className="text-ink font-medium">{humanize(span.spanName)}</span>
              <span className="text-muted numeric">{durationLabel(span.durationMs)}</span>
            </div>
            <div className="bg-line relative h-2 overflow-hidden rounded-full">
              <div className="bg-chart-secondary absolute h-full rounded-full" style={{ left: `${left}%`, width: `${width}%` }} />
            </div>
            <p className="text-muted text-xs">
              {humanize(span.spanKind)} · {span.parentSpanId ? 'Nested span' : 'Root span'} · {span.status}
            </p>
          </li>
        );
      })}
    </ol>
  );
};

const RunDetailDrawer = ({
  close,
  currentSearch,
  detailQuery,
  runId,
}: {
  close: () => void;
  currentSearch: string;
  detailQuery: ReturnType<typeof useRunDetailQuery>;
  runId: string | null;
}) => {
  const data = detailQuery.data;
  const run = data?.run;
  return (
    <Drawer
      description={run ? `${runSubtitle(run)} · ${formatTime(run.observedAt)}` : 'The selected run is loading or outside scope.'}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      open={Boolean(runId)}
      side="right"
      size="lg"
      title={run ? runTitle(run) : 'Run details'}
    >
      {detailQuery.isLoading ? <LoadingState label="Loading run detail" /> : null}
      {detailQuery.isError ? <ErrorState message={detailQuery.error.message} onRetry={() => void detailQuery.refetch()} /> : null}
      {detailQuery.data && !run ? (
        <EmptyState title="Run not found in this scope" tone="filtered">
          Close this detail view and adjust the date, environment, or run filters.
        </EmptyState>
      ) : null}
      {run && data ? (
        <div className="grid gap-6">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value={run.status} />
            <RuntimeBadge value={run.runtimeType} />
            <Badge tone="slate">{humanize(run.workloadSizeClass)}</Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="secondary">
              <Link to={{ pathname: '/explore', search: drillThroughSearch(currentSearch, { runId: run.runId, view: 'logs' }) }}>
                <ScrollText aria-hidden="true" className="size-4" />
                Contextual logs
              </Link>
            </Button>
            {data.failures?.[0] ? (
              <Button asChild variant="secondary">
                <Link
                  to={{
                    pathname: '/failures',
                    search: drillThroughSearch(currentSearch, {
                      failureId: data.failures[0].id,
                      queueView: 'all',
                    }),
                  }}
                >
                  Linked failure
                </Link>
              </Button>
            ) : null}
          </div>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Status and timing timeline</h3>
            <RunTimeline run={run} />
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Performance durations</h3>
            <KeyValueGrid
              items={[
                { key: 'queue', label: 'Queue wait', value: durationLabel(run.queueWaitDurationMs) },
                { key: 'startup', label: 'Startup', value: durationLabel(run.startupDurationMs) },
                { key: 'workload', label: 'Workload class', value: humanize(run.workloadSizeClass) },
                { key: 'end-to-end', label: 'End to end', value: durationLabel(run.endToEndDurationMs) },
              ]}
            />
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Run context</h3>
            <DetailList
              items={[
                { key: 'run-id', label: 'Run ID', value: run.runId },
                { key: 'project', label: 'Project', value: run.projectId || 'Not recorded' },
                { key: 'owner', label: 'Owner user', value: run.ownerUserId || 'Not recorded' },
                { key: 'service', label: 'Service', value: run.serviceName || 'Not recorded' },
                { key: 'job', label: 'Job kind', value: humanize(run.jobKind) },
                {
                  key: 'worker',
                  label: 'Worker version',
                  value: workerVersionLabel(run.workerImageVersion),
                },
                {
                  key: 'files',
                  label: 'Input files',
                  value: recordedInteger(run.inputFileCount),
                },
                {
                  key: 'bytes',
                  label: 'Input size',
                  value: recordedBytes(run.inputTotalBytes),
                },
              ]}
            />
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Span waterfall</h3>
            <SpanWaterfall spans={data.spans ?? []} />
          </section>
          <section className="grid gap-3">
            <h3 className="text-ink text-sm font-semibold">Linked failures</h3>
            {(data.failures ?? []).length === 0 ? (
              <EmptyState title="No failures are linked to this run" />
            ) : (
              <div className="grid gap-2">
                {(data.failures ?? []).map((failure) => (
                  <div className="border-line rounded-lg border p-3" key={failure.id}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-ink text-sm font-semibold">
                          {failure.message ?? failure.errorType ?? humanize(failure.category)}
                        </p>
                        <p className="text-muted mt-1 text-xs">{formatTime(failure.detectedAt)}</p>
                      </div>
                      <StatusBadge value={failure.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          <details className="border-line rounded-lg border">
            <summary className="text-ink cursor-pointer px-4 py-3 text-sm font-semibold">Technical run evidence</summary>
            <div className="border-line grid gap-4 border-t p-4">
              <JsonBlock
                value={{
                  attributes: run.attributes ?? {},
                  metrics: data.metricSamples ?? [],
                  relatedFacts: data.relatedFacts ?? [],
                  workloadHints: run.workloadHints ?? {},
                }}
              />
            </div>
          </details>
        </div>
      ) : null}
    </Drawer>
  );
};

const RunCard = ({ currentSearch, run }: { currentSearch: string; run: ObservabilityRunFact }) => (
  <article className="border-line grid gap-3 rounded-lg border p-4">
    <div className="flex flex-wrap items-center gap-2">
      <StatusBadge value={run.status} />
      <RuntimeBadge value={run.runtimeType} />
    </div>
    <div>
      <p className="text-ink font-semibold">{runTitle(run)}</p>
      <p className="text-muted mt-1 text-sm">{runSubtitle(run)}</p>
    </div>
    <dl className="border-line grid grid-cols-2 gap-3 border-t pt-3 text-sm">
      <div>
        <dt className="text-muted text-xs font-semibold">Duration</dt>
        <dd className="text-ink mt-1">{durationLabel(run.endToEndDurationMs)}</dd>
      </div>
      <div>
        <dt className="text-muted text-xs font-semibold">Observed</dt>
        <dd className="text-ink mt-1">{formatRelativeTime(run.observedAt)}</dd>
      </div>
    </dl>
    <Button asChild variant="secondary">
      <Link to={{ pathname: '/performance', search: updateSearch(currentSearch, { runId: run.runId, view: 'runs' }) }}>Details</Link>
    </Button>
  </article>
);

const RunsView = ({
  currentSearch,
  runSearchQuery,
  setState,
  state,
}: {
  currentSearch: string;
  runSearchQuery: ReturnType<typeof useRunSearchQuery>;
  setState: (next: Partial<AnalyticsUrlState>) => void;
  state: AnalyticsUrlState;
}) => {
  const [searchValue, setSearchValue] = useState(state.search);
  const items = runSearchQuery.data?.items ?? [];
  const hasFilters =
    Boolean(state.search) ||
    state.analysisTypes.length > 0 ||
    state.serviceNames.length > 0 ||
    state.runtimeTypes.length > 0 ||
    state.runStatuses.length > 0 ||
    state.workloadSizeClasses.length > 0 ||
    state.workerImageVersions.length > 0;

  return (
    <div className="grid min-w-0 gap-4">
      <form
        className="grid gap-3 lg:grid-cols-[minmax(16rem,1fr)_auto] lg:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          setState({ runOffset: 0, search: searchValue });
        }}
      >
        <label className="text-muted grid gap-1.5 text-xs font-semibold">
          Find a run
          <span className="flex gap-2">
            <input
              aria-label="Find a run"
              className="border-line bg-raised text-ink placeholder:text-muted/70 focus:border-accent focus:ring-accent/20 min-h-10 min-w-0 flex-1 rounded-md border px-3 text-sm outline-none focus:ring-2"
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Run ID, workflow, service, project, user, status, or worker"
              type="search"
              value={searchValue}
            />
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </span>
        </label>
        <p className="text-muted pb-2 text-xs">
          Page {Math.floor(state.runOffset / state.runPageSize) + 1} · up to {formatInteger(state.runPageSize)} runs
        </p>
      </form>

      {runSearchQuery.isLoading ? <LoadingState label="Loading run list" /> : null}
      {runSearchQuery.isError ? <ErrorState message={runSearchQuery.error.message} onRetry={() => void runSearchQuery.refetch()} /> : null}
      {runSearchQuery.data && items.length === 0 ? (
        <EmptyState
          title={hasFilters ? 'No runs match these filters' : 'No runs were recorded in this window'}
          tone={hasFilters ? 'filtered' : 'coverage'}
        >
          {hasFilters
            ? 'Clear the run search or advanced filters, or widen the selected period.'
            : 'A zero result is not treated as healthy without run collection evidence.'}
        </EmptyState>
      ) : null}
      {items.length > 0 ? (
        <Card>
          <CardHeader className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Recent runs</CardTitle>
              <p className="text-muted mt-1 text-xs">{formatInteger(items.length)} runs loaded on this page</p>
            </div>
            <p className="text-muted text-xs">Latest observed first</p>
          </CardHeader>
          <CardContent className="min-w-0">
            <div className="grid gap-3 md:hidden">
              {items.map((run) => (
                <RunCard currentSearch={currentSearch} key={run.runId} run={run} />
              ))}
            </div>
            <TableShell className="hidden md:block">
              <Table aria-label="Discoverable run list" className="table-fixed" style={{ minWidth: '64rem' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow / service</TableHead>
                    <TableHead className="w-28">Status</TableHead>
                    <TableHead className="w-32">End to end</TableHead>
                    <TableHead className="w-36">Queue / startup</TableHead>
                    <TableHead className="w-36">Observed</TableHead>
                    <TableHead className="w-28 text-right">Inspect</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((run) => (
                    <TableRow key={run.runId}>
                      <TableCell>
                        <p className="font-semibold">{runTitle(run)}</p>
                        <p className="text-muted mt-1 text-xs">{runSubtitle(run)}</p>
                      </TableCell>
                      <TableCell>
                        <StatusBadge value={run.status} />
                      </TableCell>
                      <TableCell className="numeric">{durationLabel(run.endToEndDurationMs)}</TableCell>
                      <TableCell className="numeric text-xs">
                        <p>{durationLabel(run.queueWaitDurationMs)}</p>
                        <p className="text-muted mt-1">{durationLabel(run.startupDurationMs)}</p>
                      </TableCell>
                      <TableCell>
                        <p>{formatRelativeTime(run.observedAt)}</p>
                        <p className="text-muted mt-1 text-xs">{formatTime(run.observedAt)}</p>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="ghost">
                          <Link
                            to={{
                              pathname: '/performance',
                              search: updateSearch(currentSearch, { runId: run.runId, view: 'runs' }),
                            }}
                          >
                            Details
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          </CardContent>
        </Card>
      ) : null}
      {runSearchQuery.data ? (
        <div className="flex items-center justify-between gap-3">
          <Button
            disabled={state.runOffset === 0}
            onClick={() => setState({ runOffset: Math.max(state.runOffset - state.runPageSize, 0), runId: null })}
            variant="secondary"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            Previous
          </Button>
          <Button
            disabled={!runSearchQuery.data.hasMore}
            onClick={() => setState({ runOffset: state.runOffset + state.runPageSize, runId: null })}
            variant="secondary"
          >
            Next
            <ArrowRight aria-hidden="true" className="size-4" />
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const TimingView = ({
  slowSpans,
  slowWorkflows,
  state,
  summary,
}: {
  slowSpans: ObservabilityCompareBucket[];
  slowWorkflows: ObservabilityCompareBucket[];
  state: AnalyticsUrlState;
  summary: ReturnType<typeof useSummaryQuery>['data'];
}) => {
  const diagnostics = summary?.diagnostics;
  const bottlenecks = diagnostics?.bottlenecks ?? [];
  const drift = diagnostics?.drift ?? [];
  const efficiency = diagnostics?.efficiency ?? [];
  const criticalPath = diagnostics?.criticalPath ?? [];
  const versionDrift = diagnostics?.versionDrift ?? [];
  return (
    <div className="grid gap-4">
      <section className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader className="flex items-center gap-2">
            <Clock3 aria-hidden="true" className="text-accent size-4" />
            <CardTitle>Run duration distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <PercentileChart label="Run duration" percentiles={summary?.runs.durations.runDuration} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Stage comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <StageComparison percentile={state.percentile} summary={summary} />
          </CardContent>
        </Card>
      </section>
      <section className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Slowest workflows</CardTitle>
            <p className="text-muted mt-1 text-xs">Ranked by P95 run duration with sample count.</p>
          </CardHeader>
          <CardContent>
            <RankedDurationList buckets={slowWorkflows} emptyTitle="No workflow duration ranking" label="run" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Slowest spans</CardTitle>
            <p className="text-muted mt-1 text-xs">Ranked by P95 span duration with sample count.</p>
          </CardHeader>
          <CardContent>
            <RankedDurationList buckets={slowSpans} emptyTitle="No span duration ranking" label="span" />
          </CardContent>
        </Card>
      </section>
      <section className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Workflow outcomes</CardTitle>
            <p className="text-muted mt-1 text-xs">Measured run outcomes and explicit durable-result, view, and download events.</p>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div>
                <p className="text-muted text-xs">Completion</p>
                <p className="numeric mt-1 font-semibold">{rateLabel(diagnostics?.outcomes.completionRate)}</p>
              </div>
              <div>
                <p className="text-muted text-xs">Failure</p>
                <p className="numeric mt-1 font-semibold">{rateLabel(diagnostics?.outcomes.failureRate)}</p>
              </div>
              <div>
                <p className="text-muted text-xs">Resolved</p>
                <p className="numeric mt-1 font-semibold">{formatInteger(diagnostics?.failureOutcomes.resolvedFailures)}</p>
              </div>
              <div>
                <p className="text-muted text-xs">Ignored</p>
                <p className="numeric mt-1 font-semibold">{formatInteger(diagnostics?.failureOutcomes.ignoredFailures)}</p>
              </div>
            </div>
            <div className="border-line grid grid-cols-2 gap-3 border-t pt-3">
              <div>
                <p className="text-muted text-xs">P50 first progress</p>
                <p className="numeric mt-1">{durationLabel(diagnostics?.outcomes.timeToFirstProgress.p50)}</p>
              </div>
              <div>
                <p className="text-muted text-xs">P50 resolution</p>
                <p className="numeric mt-1">{durationLabel(diagnostics?.failureOutcomes.timeToResolution.p50)}</p>
              </div>
              <div>
                <p className="text-muted text-xs">Rerun / recovered / stalled</p>
                <p className="numeric mt-1">
                  {formatInteger(diagnostics?.outcomes.rerunRuns)} / {formatInteger(diagnostics?.outcomes.recoveredRuns)} /{' '}
                  {formatInteger(diagnostics?.outcomes.stalledRuns)}
                </p>
              </div>
              <div>
                <p className="text-muted text-xs">Result views / completed downloads</p>
                <p className="numeric mt-1">
                  {formatInteger(diagnostics?.outcomes.resultViews)} / {formatInteger(diagnostics?.outcomes.completedDownloads)}
                </p>
              </div>
              <div>
                <p className="text-muted text-xs">P50 first durable result</p>
                <p className="numeric mt-1">{durationLabel(diagnostics?.outcomes.timeToFirstDurableResult?.p50)}</p>
              </div>
              <div>
                <p className="text-muted text-xs">P50 retry recovery</p>
                <p className="numeric mt-1">{durationLabel(diagnostics?.outcomes.timeToRecovery?.p50)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Window-over-window drift</CardTitle>
            <p className="text-muted mt-1 text-xs">
              P50 run duration versus the immediately preceding equal window. At least three timed runs per window are required.
            </p>
          </CardHeader>
          <CardContent className="grid gap-2">
            {drift.length === 0 ? <EmptyState title="No comparable workflow windows" tone="coverage" /> : null}
            {drift.map((item) => (
              <div className="border-line flex items-center justify-between gap-3 border-b py-2 last:border-0" key={item.analysisType}>
                <div className="min-w-0">
                  <p className="truncate font-semibold">{humanize(item.analysisType)}</p>
                  <p className="text-muted text-xs">
                    {formatInteger(item.currentTimedRuns)} current · {formatInteger(item.previousTimedRuns)} previous
                  </p>
                </div>
                <div className="text-right">
                  <Badge tone={driftTone(item.direction)}>{humanize(item.direction)}</Badge>
                  <p className="numeric text-muted mt-1 text-xs">{rateLabel(item.relativeChange)}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
      <section className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Measured critical-path shares</CardTitle>
            <p className="text-muted mt-1 text-xs">
              Shares are allocated into mutually exclusive end-to-end buckets; explicit persistence and callback spans take priority over
              overlapping scientific parent spans, and uncovered time remains unclassified.
            </p>
          </CardHeader>
          <CardContent className="grid gap-2">
            {criticalPath.length === 0 ? <EmptyState title="No complete critical-path evidence" tone="coverage" /> : null}
            {criticalPath.map((item) => (
              <div className="border-line border-b py-2 last:border-0" key={item.analysisType}>
                <div className="flex justify-between gap-3">
                  <p className="font-semibold">{humanize(item.analysisType)}</p>
                  <p className="text-muted numeric text-xs">{formatInteger(item.runs)} timed runs</p>
                </div>
                <p className="text-muted mt-1 text-xs">
                  queue {rateLabel(item.queueWaitShare)} · startup {rateLabel(item.startupShare)} · image pull{' '}
                  {rateLabel(item.imagePullShare)} · scientific {rateLabel(item.scientificStageShare)} · persist{' '}
                  {rateLabel(item.resultPersistenceShare)} · callback {rateLabel(item.callbackShare)} · unclassified{' '}
                  {rateLabel(item.unclassifiedShare)}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Worker-version drift</CardTitle>
            <p className="text-muted mt-1 text-xs">Latest two worker versions with at least three timed runs each.</p>
          </CardHeader>
          <CardContent className="grid gap-2">
            {versionDrift.length === 0 ? <EmptyState title="No comparable worker versions" tone="coverage" /> : null}
            {versionDrift.map((item) => (
              <div
                className="border-line flex justify-between gap-3 border-b py-2 last:border-0"
                key={`${item.analysisType}:${item.currentVersion}`}
              >
                <div className="min-w-0">
                  <p className="font-semibold">{humanize(item.analysisType)}</p>
                  <p className="text-muted truncate text-xs">
                    {item.baselineVersion} → {item.currentVersion}
                  </p>
                </div>
                <div className="text-right">
                  <Badge tone={driftTone(item.direction)}>{humanize(item.direction)}</Badge>
                  <p className="text-muted numeric mt-1 text-xs">{rateLabel(item.relativeChange)}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
      <section className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recorded stage bottlenecks</CardTitle>
            <p className="text-muted mt-1 text-xs">
              Total recorded span time and failure frequency; this is not an inferred workflow critical path.
            </p>
          </CardHeader>
          <CardContent className="grid gap-2">
            {bottlenecks.length === 0 ? <EmptyState title="No stage timing evidence" tone="coverage" /> : null}
            {bottlenecks.map((item) => (
              <div
                className="border-line grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b py-2 last:border-0"
                key={`${item.analysisType ?? 'unknown'}:${item.spanName}`}
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold">{humanize(item.spanName)}</p>
                  <p className="text-muted truncate text-xs">
                    {humanize(item.analysisType)} · {formatInteger(item.observations)} samples · {formatInteger(item.failedSpans)} failed
                  </p>
                </div>
                <div className="text-right">
                  <p className="numeric font-semibold">{durationLabel(item.duration.p95)}</p>
                  <p className="text-muted text-xs">P95 · {rateLabel(item.recordedStageTimeShare)} of recorded stage time</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Input-normalized efficiency</CardTitle>
            <p className="text-muted mt-1 text-xs">
              Only runs with measured input bytes and duration; grouped by workflow, workload class, and worker version.
            </p>
          </CardHeader>
          <CardContent className="grid gap-2">
            {efficiency.length === 0 ? <EmptyState title="No normalized efficiency evidence" tone="coverage" /> : null}
            {efficiency.map((item) => (
              <div
                className="border-line grid grid-cols-[minmax(0,1fr)_auto] gap-3 border-b py-2 last:border-0"
                key={`${item.analysisType}:${item.workloadSizeClass}:${item.workerImageVersion ?? 'unknown'}`}
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold">{humanize(item.analysisType)}</p>
                  <p className="text-muted truncate text-xs">
                    {humanize(item.workloadSizeClass)} · {workerVersionLabel(item.workerImageVersion)} · {formatCount(item.runs, 'run')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="numeric font-semibold">
                    {typeof item.inputThroughput.p50 === 'number' ? `${item.inputThroughput.p50.toFixed(2)} MiB/s` : 'Not measured'}
                  </p>
                  <p className="text-muted text-xs">P50 throughput</p>
                  <p className="text-muted numeric mt-1 text-xs">
                    {typeof item.estimatedCostPerInputGb?.p50 === 'number'
                      ? `$${item.estimatedCostPerInputGb.p50.toFixed(4)}/GiB`
                      : 'Cost/GiB not measured'}
                    {' · '}
                    {typeof item.estimatedCostPerInputFile?.p50 === 'number'
                      ? `$${item.estimatedCostPerInputFile.p50.toFixed(4)}/file`
                      : 'cost/file not measured'}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

const StartupTrend = ({ runs }: { runs: ObservabilityRunFact[] }) => {
  const samples = runs
    .filter((run): run is ObservabilityRunFact & { startupDurationMs: number } => typeof run.startupDurationMs === 'number')
    .toSorted((left, right) => new Date(left.observedAt).getTime() - new Date(right.observedAt).getTime())
    .slice(-16);
  const maximum = Math.max(...samples.map((run) => run.startupDurationMs), 0);

  if (samples.length < 2 || maximum === 0) {
    return (
      <EmptyState title="Not enough recent startup observations" tone="coverage">
        At least two duration-bearing runs are needed for a recent-observation trend.
      </EmptyState>
    );
  }

  return (
    <div className="grid gap-3">
      <div
        aria-label={`Recent startup observations from ${durationLabel(samples[0]?.startupDurationMs)} to ${durationLabel(samples.at(-1)?.startupDurationMs)}`}
        className="flex h-36 items-end gap-1"
        role="img"
      >
        {samples.map((run) => (
          <div
            className="bg-chart-secondary hover:bg-accent min-w-1 flex-1 rounded-t-sm transition-colors"
            key={run.runId}
            style={{ height: `${Math.max((run.startupDurationMs / maximum) * 100, 4)}%` }}
            title={`${formatTime(run.observedAt)} · ${durationLabel(run.startupDurationMs)}`}
          />
        ))}
      </div>
      <p className="text-muted text-xs">
        {formatInteger(samples.length)} duration-bearing runs from the currently loaded latest page, ordered by observation time.
      </p>
    </div>
  );
};

const StartupView = ({
  recentRuns,
  runtimeBuckets,
  serviceBuckets,
  summary,
}: {
  recentRuns: ObservabilityRunFact[];
  runtimeBuckets: ObservabilityCompareBucket[];
  serviceBuckets: ObservabilityCompareBucket[];
  summary: ReturnType<typeof useSummaryQuery>['data'];
}) => {
  const startup = summary?.runs.durations.startupDuration;
  const startupCount = startup?.count ?? 0;
  const totalRuns = summary?.runs.totalRuns ?? 0;

  return (
    <div className="grid gap-4">
      <StateView
        title={
          startupCount > 0
            ? `Startup duration recorded for ${formatInteger(startupCount)} of ${formatInteger(totalRuns)} runs`
            : 'Startup duration is not recorded in this scope'
        }
        tone={startupCount > 0 ? 'neutral' : 'coverage'}
      >
        This is field presence, not instrumentation completeness. Cold-start classification is unavailable, so startup duration is not
        labelled as a cold start.
      </StateView>
      <section className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader className="flex items-center gap-2">
            <Gauge aria-hidden="true" className="text-accent size-4" />
            <CardTitle>Startup duration distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <PercentileChart label="Startup duration" percentiles={startup} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Recent startup observations</CardTitle>
          </CardHeader>
          <CardContent>
            <StartupTrend runs={recentRuns} />
          </CardContent>
        </Card>
      </section>
      <section className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Startup by runtime</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedDurationList buckets={runtimeBuckets} emptyTitle="No runtime startup comparison" label="startup" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Startup by service</CardTitle>
          </CardHeader>
          <CardContent>
            <RankedDurationList buckets={serviceBuckets} emptyTitle="No service startup comparison" label="startup" />
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

const queryState = (queries: { error: Error | null; isError: boolean; isLoading: boolean; refetch: () => unknown }[]): ReactNode => {
  if (queries.some((query) => query.isLoading)) return <LoadingState label="Loading performance evidence" />;
  const failed = queries.find((query) => query.isError);
  if (failed) return <ErrorState message={failed.error?.message ?? 'Performance evidence failed'} onRetry={() => void failed.refetch()} />;
  return null;
};

export const PerformancePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setState, state } = useRouteContext();
  const view = currentPerformanceView(location.search);
  const summaryQuery = useSummaryQuery(state);
  const runSearchQuery = useRunSearchQuery(state, view !== 'timing');
  const detailQuery = useRunDetailQuery(state);
  const slowWorkflowsQuery = usePerformanceCompareQuery(state, 'run_duration', 'analysis_type', view === 'timing');
  const slowSpansQuery = usePerformanceCompareQuery(state, 'span_duration', 'span_name', view === 'timing');
  const startupRuntimeQuery = usePerformanceCompareQuery(state, 'startup_duration', 'runtime_type', view === 'startup');
  const startupServiceQuery = usePerformanceCompareQuery(state, 'startup_duration', 'service_name', view === 'startup');
  const runsEvidence = summaryQuery.data?.signalEvidence?.find((evidence) => evidence.signal === 'runs');
  const spansEvidence = summaryQuery.data?.signalEvidence?.find((evidence) => evidence.signal === 'spans');
  let supportingQueries: Parameters<typeof queryState>[0] = [];
  if (view === 'timing') supportingQueries = [slowWorkflowsQuery, slowSpansQuery];
  if (view === 'startup') supportingQueries = [runSearchQuery, startupRuntimeQuery, startupServiceQuery];
  const supportingState = queryState(supportingQueries);

  const setView = (nextView: string) => {
    void navigate(
      {
        pathname: '/performance',
        search: updateSearch(location.search, {
          runId: nextView === 'runs' ? state.runId : null,
          view: nextView,
        }),
      },
      { replace: true },
    );
  };

  if (summaryQuery.isLoading) return <LoadingState label="Loading performance workspace" />;
  if (summaryQuery.isError) return <ErrorState message={summaryQuery.error.message} onRetry={() => void summaryQuery.refetch()} />;

  return (
    <div className="grid min-w-0 gap-5">
      <RunDetailDrawer
        close={() =>
          void navigate(
            {
              pathname: '/performance',
              search: updateSearch(location.search, { runId: null }),
            },
            { replace: true },
          )
        }
        currentSearch={location.search}
        detailQuery={detailQuery}
        runId={state.runId}
      />

      <section className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent>
            <p className="text-muted text-xs font-semibold">Runs in scope</p>
            <p className="text-ink numeric mt-1 text-2xl font-semibold">{formatInteger(summaryQuery.data?.runs.totalRuns)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-muted text-xs font-semibold">Failed runs</p>
            <p className="text-critical numeric mt-1 text-2xl font-semibold">{formatInteger(summaryQuery.data?.runs.failedRuns)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <p className="text-muted text-xs font-semibold">Evidence freshness</p>
            <p className="text-ink mt-1 text-sm font-semibold">
              {runsEvidence?.lastReceivedAt ? formatRelativeTime(runsEvidence.lastReceivedAt) : 'No run receipt recorded'}
            </p>
          </CardContent>
        </Card>
      </section>

      <Tabs onValueChange={setView} value={view}>
        <div className="overflow-x-auto pb-1">
          <TabsList>
            <TabsTrigger value="runs">Runs</TabsTrigger>
            <TabsTrigger value="timing">Timing</TabsTrigger>
            <TabsTrigger value="startup">Startup</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="runs">
          <RunsView currentSearch={location.search} key={state.search} runSearchQuery={runSearchQuery} setState={setState} state={state} />
        </TabsContent>
        <TabsContent value="timing">
          {supportingState ?? (
            <TimingView
              slowSpans={slowSpansQuery.data?.buckets ?? []}
              slowWorkflows={slowWorkflowsQuery.data?.buckets ?? []}
              state={state}
              summary={summaryQuery.data}
            />
          )}
        </TabsContent>
        <TabsContent value="startup">
          {supportingState ?? (
            <StartupView
              recentRuns={runSearchQuery.data?.items ?? []}
              runtimeBuckets={startupRuntimeQuery.data?.buckets ?? []}
              serviceBuckets={startupServiceQuery.data?.buckets ?? []}
              summary={summaryQuery.data}
            />
          )}
        </TabsContent>
      </Tabs>

      <div className="text-muted flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span>
          Runs: {formatInteger(runsEvidence?.observations)} observations
          {runsEvidence?.lastObservedAt ? ` · latest ${formatRelativeTime(runsEvidence.lastObservedAt)}` : ''}
        </span>
        <span>
          Spans: {formatInteger(spansEvidence?.observations)} observations
          {spansEvidence?.lastObservedAt ? ` · latest ${formatRelativeTime(spansEvidence.lastObservedAt)}` : ''}
        </span>
        <span>Generated {formatTime(summaryQuery.data?.generatedAt)}</span>
      </div>
    </div>
  );
};
